import { trpc } from '@klear/ui/lib/trpc'
import { Badge } from '@klear/ui/components/badge'
import { Button } from '@klear/ui/components/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@klear/ui/components/card'
import LoadingSpinner from '@klear/ui/components/spinner'
import { ExternalLink, Receipt } from 'lucide-react'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { PERMISSION_ENTITY_TREASURY, PERMISSION_ACTION_VIEW, PERMISSION_ACTION_EDIT } from '@klear/constants'
import { KlearDate } from '@klear/date'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

function Detail({ label, value }: { label: string; value: string | null }) {
  if (!value) {
    return null
  }
  return (
    <div className="flex items-baseline justify-between gap-4 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  )
}

export default function Billing() {
  useDocumentTitle('Billing')
  const { user } = useAuth()
  const canViewBilling = user.hasPermission(PERMISSION_ENTITY_TREASURY, PERMISSION_ACTION_VIEW)
  const canManageBilling = user.hasPermission(PERMISSION_ENTITY_TREASURY, PERMISSION_ACTION_EDIT)
  const { data: billing, isLoading } = trpc.subscription.myBilling.useQuery(undefined, { enabled: canViewBilling })
  const portal = trpc.subscription.createBillingPortalSession.useMutation({
    onSuccess: ({ url }) => {
      window.location.href = url
    }
  })

  if (!canViewBilling) {
    return null
  }

  if (isLoading) {
    return <LoadingSpinner />
  }

  const isOwed = billing?.isOwed ?? false
  const amount = billing?.amount ? `${billing.amount.toString()} ${billing.frequency ?? ''}`.trim() : null

  return (
    <div className="flex flex-col p-6 gap-6">
      <h1 className="text-2xl font-bold">Billing</h1>

      <div className="max-w-md">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Current Plan
            </CardTitle>
            <CardDescription>Your subscription tier</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-sm text-muted-foreground">Plan</div>
                <div className="text-lg font-semibold">{billing?.tier ?? '—'}</div>
              </div>
              {billing?.status && (
                <Badge variant={isOwed ? 'destructive' : 'secondary'} className="capitalize">
                  {billing.status.replace(/_/g, ' ')}
                </Badge>
              )}
            </div>

            <Detail label="Amount" value={amount} />
            <Detail label="Started" value={billing?.startDate ? KlearDate.format(billing.startDate) : null} />
            {billing?.renewalDate && (
              <Detail label={billing.renewalDateLabel} value={KlearDate.format(billing.renewalDate)} />
            )}

            {isOwed && billing?.pastDueInvoiceUrl && (
              <a
                href={billing.pastDueInvoiceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm font-medium text-destructive underline"
              >
                Payment past due — view invoice <ExternalLink className="h-3 w-3" />
              </a>
            )}

            {billing?.hasBillingAccount && canManageBilling && (
              <>
                <Button
                  type="button"
                  variant="link"
                  className="h-auto p-0 gap-1 self-start underline underline-offset-auto [&_svg]:size-3"
                  disabled={portal.isPending}
                  onClick={() => portal.mutate()}
                >
                  Manage billing <ExternalLink className="h-3 w-3" />
                </Button>
                {portal.isError && (
                  <p className="text-xs text-destructive">
                    Couldn&apos;t open Stripe billing. Contact your account manager for help.
                  </p>
                )}
              </>
            )}

            <p className="text-xs text-muted-foreground mt-3">
              {billing?.hasBillingAccount && canManageBilling
                ? 'View invoices and update your card or billing address in Manage billing. Contact your account manager to change your plan.'
                : 'Contact your account manager for anything to do with your plan or billing.'}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
